import { isProxyRequest } from '@lemonade-hq/uniclient-chat';
import type { UniclientFrameType } from '@lemonade-hq/uniclient-chat';
import { nativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import { useEffect, useRef, useState } from 'react';
import { useAsyncTriggerCallback } from './useAsyncTriggerCallback';

interface UseTransitionBetweenScriptsProps {
  readonly scriptNameFromPath: string;
  readonly isInitializing: boolean;
  readonly presentationLock: React.MutableRefObject<boolean>;
}

export interface UseTransitionBetweenScriptsReturn {
  readonly isTransitioningBetweenScripts: boolean;
  readonly createTransitionTriggerCallback: () => Promise<void>;
  readonly transitionTriggerCallback: React.RefObject<(() => void) | null>;
  readonly scriptName: React.MutableRefObject<string>;
  readonly isTransitioningForward: React.MutableRefObject<boolean>;
  readonly isTransitioningToSameScript: React.MutableRefObject<boolean>;
  readonly lastTransitioningFrameType: React.MutableRefObject<UniclientFrameType>;
}

export const useTransitionBetweenScripts = ({
  scriptNameFromPath,
  isInitializing,
  presentationLock,
}: UseTransitionBetweenScriptsProps): UseTransitionBetweenScriptsReturn => {
  const [isTransitioningBetweenScripts, setIsTransitioningBetweenScripts] = useState(false);
  const [createTransitionTriggerCallback, transitionTriggerCallback] = useAsyncTriggerCallback();
  const scriptName = useRef<string>('');
  const isTransitioningForward = useRef(false);
  const isTransitioningToSameScript = useRef(false);
  const lastTransitioningFrameType = useRef<UniclientFrameType>();
  const didRemoveNativePageLoader = useRef(false);

  useEffect(() => {
    /* istanbul ignore else -- @preserve - since we initiate `scriptName` with empty string, this use effect will always run when the script names are different */
    if (scriptName.current !== scriptNameFromPath || isTransitioningToSameScript.current) {
      presentationLock.current = true;
      setIsTransitioningBetweenScripts(true);
    }

    let timeout: NodeJS.Timeout | undefined;

    if (!isInitializing) {
      const releaseLock = (): void => {
        scriptName.current = scriptNameFromPath;
        setIsTransitioningBetweenScripts(false);
        presentationLock.current = false;
        isTransitioningToSameScript.current = false;
        isTransitioningForward.current = false;
      };

      // in localhost, initializing the script happens immediately (since the server is local)
      // adding a slight delay to ensure transition has finished before releasing the lock
      /* istanbul ignore next -- @preserve - this is a workaround to ensure the lock is released after the transition has finished */
      if (window.lemonade?.stage === 'local' && !isProxyRequest(window.location.pathname)) {
        timeout = setTimeout(releaseLock, 10);
      } else {
        releaseLock();
      }

      if (!didRemoveNativePageLoader.current) {
        void nativeApp.pageLoaded();
        didRemoveNativePageLoader.current = true;
      }
    }

    return (): void => {
      clearTimeout(timeout);
    };
  }, [scriptNameFromPath, isInitializing, presentationLock]);

  return {
    isTransitioningBetweenScripts,
    createTransitionTriggerCallback,
    transitionTriggerCallback,
    scriptName,
    isTransitioningForward,
    isTransitioningToSameScript,
    lastTransitioningFrameType,
  };
};
